import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { Grid, Card, CardMedia, Container, Typography, Breadcrumbs, Link, IconButton, Tooltip, Box, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import Loading from './Loading';

const AlbumViewer = () => {
  const { albumKey } = useParams();
  const [photos, setPhotos] = useState([]);
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlbumPhotos = async () => {
      try {
        // Construct the URL for album info using the base API URL
        const albumInfoUrl = `${process.env.REACT_APP_SMUGMUG_API_URL}?smugmug_path=/album/${albumKey}`;
        const albumInfoResponse = await axios.get(albumInfoUrl);
        if (albumInfoResponse.data?.Response?.Album) {
          setAlbum(albumInfoResponse.data.Response.Album);
        }

        // Construct the URL for image info using the base API URL
        const imagesUrl = `${process.env.REACT_APP_SMUGMUG_API_URL}?smugmug_path=/album/${albumKey}!images`;
        const imagesResponse = await axios.get(imagesUrl);
        
        if (imagesResponse.data?.Response?.AlbumImage) {
          setPhotos(imagesResponse.data.Response.AlbumImage);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching SmugMug album photos via proxy:", error);
        setLoading(false);
      }
    };

    fetchAlbumPhotos();
  }, [albumKey]);

  if (loading) {
    return <Container sx={{ py: 4 }}><Loading /></Container>;
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link component={RouterLink} underline="hover" color="inherit" to="/photos">
            All Albums
          </Link>
        </Breadcrumbs>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', textAlign: 'center', flexGrow: 1 }}>
          {album?.Name || 'Photo Album'}
        </Typography>
        {album ? (
          <Button
            variant="outlined"
            href={album.WebUri}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on SmugMug
          </Button>
        ) : (
          <Box sx={{ width: 150 }} /> 
        )}
      </Box>

      <Grid container spacing={2}>
        {photos.map((photo) => {
          const imageUrl = photo.ThumbnailUrl.replace('/Th/', '/M/');
          
          return (
            <Grid item key={photo.ImageKey} xs={12} sm={6} md={4} lg={3}>
              <Card sx={{ position: 'relative', '&:hover .download-button': { opacity: 1 } }}>
                <Link href={photo.WebUri} target="_blank" rel="noopener noreferrer">
                  <CardMedia
                    component="img"
                    image={imageUrl}
                    alt={photo.Title || 'SmugMug Image'}
                    sx={{ aspectRatio: '1 / 1', objectFit: 'cover' }}
                  />
                </Link>
                <Tooltip title="Download Original">
                  <IconButton
                    className="download-button"
                    component="a"
                    href={photo.ArchivedUri}
                    download
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default AlbumViewer;
